import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass,
  Building2,
  User,
  Mail,
  Lock,
  MapPin,
  Landmark,
  ImagePlus,
  X,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OBEDIENCIAS = [
  "GOB - Grande Oriente do Brasil",
  "COMAB - Confederação da Maçonaria Simbólica do Brasil",
  "CMSB - Confederação Maçônica Simbólica do Brasil",
  "GLMMG - Grande Loja Maçônica de MG",
  "GLESP - Grande Loja do Estado de SP",
  "Outra (informar em endereço)",
];

export const LojaSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingPlan = searchParams.get("plan") || (typeof window !== "undefined" && sessionStorage.getItem("pending_plan_lookup")) || null;
  const referralCode = searchParams.get("ref") || null;
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [redirectingCheckout, setRedirectingCheckout] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    loja_name: "",
    loja_number: "",
    oriente: "",
    obediencia: OBEDIENCIAS[0],
    address: "",
    loja_email: "",
    loja_phone: "",
    logo_b64: null,
    admin_name: "",
    admin_email: "",
    admin_password: "",
    admin_password_confirm: "",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo muito grande (máx 500 KB). Comprima antes de enviar.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => upd("logo_b64", ev.target.result);
    reader.readAsDataURL(file);
  };

  const validateStep1 = () => {
    if (!form.loja_name.trim() || !form.loja_number.trim() || !form.oriente.trim()) {
      toast.error("Preencha Nome, Número e Oriente da Loja.");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.admin_name.trim() || !form.admin_email.trim() || !form.admin_password.trim()) {
      toast.error("Preencha os dados do Venerável Mestre.");
      return false;
    }
    if (form.admin_password.length < 6) {
      toast.error("Senha deve ter no mínimo 6 caracteres.");
      return false;
    }
    if (form.admin_password !== form.admin_password_confirm) {
      toast.error("As senhas não conferem.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      delete payload.admin_password_confirm;
      if (referralCode) payload.referral_code = referralCode;
      const res = await axios.post(`${API}/lojas/signup`, payload);
      toast.success(res.data.message);

      // Se veio da /pricing com um plano pré-selecionado, dispara o checkout
      if (pendingPlan) {
        try {
          setRedirectingCheckout(true);
          sessionStorage.removeItem("pending_plan_lookup");
          const co = await axios.post(`${API}/payments/checkout`, {
            lookup_key: pendingPlan,
            origin_url: window.location.origin,
            loja_id: res.data.loja.id,
            email: res.data.admin_user.email,
          });
          if (co.data.checkout_url) {
            window.location.href = co.data.checkout_url;
            return;
          }
        } catch (e) {
          toast.error("Loja criada, mas falha ao iniciar checkout. Faça login e assine pelo painel.");
          setRedirectingCheckout(false);
        }
      }

      setSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar loja.");
    } finally {
      setSubmitting(false);
    }
  };

  if (redirectingCheckout) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6" data-testid="signup-redirecting-checkout">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto" />
          <h1 className="mt-6 text-2xl font-serif font-bold text-white">Loja criada. Redirecionando ao pagamento…</h1>
          <p className="mt-3 text-sm text-slate-400">
            Estamos abrindo o checkout seguro do Stripe. Aguarde um instante.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6" data-testid="signup-success-view">
        <div className="w-full max-w-lg bg-[#0B111E] border border-emerald-500/40 rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-6 py-8 text-center border-b border-emerald-500/20">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>
            <div className="text-[10px] tracking-widest uppercase text-emerald-400 font-bold">
              À Glória do G∴A∴D∴U∴
            </div>
            <h1 className="text-2xl font-serif font-bold text-white mt-1">
              Loja Cadastrada com Sucesso!
            </h1>
            <p className="text-sm text-slate-400 mt-2">
              Bem-vindo(a) à plataforma <strong className="text-[#D4AF37]">Lojas Integradas</strong>.
            </p>
          </div>
          <div className="p-6 space-y-4 text-sm text-slate-300">
            <div className="bg-[#131C2E] rounded-xl p-4 space-y-1.5 border border-slate-800">
              <div><strong className="text-[#D4AF37]">Loja:</strong> {success.loja.name} nº {success.loja.number}</div>
              <div><strong className="text-slate-400">Oriente:</strong> {success.loja.oriente}</div>
              <div><strong className="text-slate-400">Venerável:</strong> {success.admin_user.name}</div>
              <div><strong className="text-slate-400">Login:</strong> <span className="font-mono text-xs">{success.admin_user.email}</span></div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              A partir de agora, entre no sistema com o e-mail e senha cadastrados. Você poderá adicionar Irmãos, agendar Sessões, gerenciar a Tesouraria e imprimir Balaústres com o brasão da sua Loja.
            </p>
            <button
              data-testid="signup-btn-login"
              onClick={() => navigate("/")}
              className="w-full flex items-center justify-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold py-3 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            >
              <ArrowRight className="w-4 h-4" />
              <span>Entrar no Sistema</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6" data-testid="signup-page">
      <div className="w-full max-w-2xl bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden">
        {referralCode && (
          <div
            data-testid="signup-referral-banner"
            className="bg-emerald-500/10 border-b border-emerald-500/30 px-6 py-3 text-center text-xs text-emerald-300"
          >
            🎁 Você foi indicado com o código{" "}
            <strong className="font-mono text-white">{referralCode}</strong>. Ao concluir seu cadastro e ativar um plano pago, a Loja indicadora ganha 1 mês grátis.
          </div>
        )}
        <div className="px-6 py-6 bg-[#080D18] border-b border-[#D4AF37]/20 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-3">
            <Compass className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <div className="text-[10px] tracking-widest uppercase text-[#D4AF37] font-bold">
            À Glória do Grande Arquiteto do Universo
          </div>
          <h1 className="text-2xl font-serif font-bold text-white mt-1">Cadastro de Loja Maçônica</h1>
          <p className="text-xs text-slate-400 mt-1">
            Cadastre sua Augusta Loja na plataforma <strong className="text-[#D4AF37]">Lojas Integradas</strong>. Gratuito, isolado e seguro.
          </p>

          {/* Stepper */}
          <div className="mt-5 flex items-center justify-center space-x-2 text-[10px] uppercase tracking-wider">
            <StepDot n={1} active={step === 1} done={step > 1} label="Loja" />
            <div className={`w-8 h-[2px] ${step > 1 ? "bg-[#D4AF37]" : "bg-slate-700"}`}></div>
            <StepDot n={2} active={step === 2} done={step > 2} label="Venerável" />
          </div>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-[#D4AF37] font-bold flex items-center space-x-2 mb-1">
                <Building2 className="w-3.5 h-3.5" />
                <span>Dados da Augusta Oficina</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Nome da Loja *" testid="signup-loja-name">
                  <input required value={form.loja_name} onChange={(e) => upd("loja_name", e.target.value)} placeholder="A.R.L.S. Estrela Polar" className="inp" data-testid="signup-input-loja-name" />
                </Field>
                <Field label="Número *" testid="signup-loja-number">
                  <input required value={form.loja_number} onChange={(e) => upd("loja_number", e.target.value)} placeholder="123" className="inp" data-testid="signup-input-loja-number" />
                </Field>
                <Field label="Oriente (Cidade) *">
                  <input required value={form.oriente} onChange={(e) => upd("oriente", e.target.value)} placeholder="São Paulo" className="inp" data-testid="signup-input-oriente" />
                </Field>
                <Field label="Obediência / Potência">
                  <select value={form.obediencia} onChange={(e) => upd("obediencia", e.target.value)} className="inp" data-testid="signup-input-obediencia">
                    {OBEDIENCIAS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="E-mail da Loja">
                  <input type="email" value={form.loja_email} onChange={(e) => upd("loja_email", e.target.value)} placeholder="secretaria@loja.org.br" className="inp" data-testid="signup-input-loja-email" />
                </Field>
                <Field label="Telefone">
                  <input value={form.loja_phone} onChange={(e) => upd("loja_phone", e.target.value)} placeholder="(11) 3232-7890" className="inp" data-testid="signup-input-loja-phone" />
                </Field>
              </div>

              <Field label="Endereço do Templo">
                <input value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Rua da Aurora, 1717 - São Paulo/SP" className="inp" data-testid="signup-input-address" />
              </Field>

              <div>
                <div className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold mb-1.5">Brasão / Logo da Loja (opcional)</div>
                {form.logo_b64 ? (
                  <div className="flex items-center space-x-3 bg-[#131C2E] rounded-xl p-3 border border-[#D4AF37]/30">
                    <img src={form.logo_b64} alt="Brasão" className="w-16 h-16 bg-white rounded-lg object-contain p-1" data-testid="signup-logo-preview" />
                    <div className="flex-1 text-xs text-slate-300">
                      <div className="font-semibold text-white">Brasão carregado</div>
                      <div className="text-[10px] text-slate-500">Aparecerá em balaústres e boletins.</div>
                    </div>
                    <button type="button" onClick={() => upd("logo_b64", null)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center h-16 border-2 border-dashed border-slate-600 rounded-xl text-slate-400 hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer text-xs font-semibold uppercase tracking-wider transition-colors">
                    <ImagePlus className="w-4 h-4 mr-2" />
                    Clique para enviar PNG (máx 500 KB)
                    <input data-testid="signup-input-logo" type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} className="hidden" />
                  </label>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-800">
                <button
                  data-testid="signup-btn-next"
                  onClick={() => validateStep1() && setStep(2)}
                  className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
                >
                  <span>Próximo — Venerável Mestre</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-[#D4AF37] font-bold flex items-center space-x-2 mb-1">
                <User className="w-3.5 h-3.5" />
                <span>Venerável Mestre Inicial</span>
              </div>
              <p className="text-[11px] text-slate-400 -mt-1 mb-3 italic">
                Este será o primeiro usuário com acesso administrativo total à Loja. Poderá cadastrar demais Irmãos depois.
              </p>

              <Field label="Nome completo do Venerável *">
                <input required value={form.admin_name} onChange={(e) => upd("admin_name", e.target.value)} placeholder="Ir. Fulano de Tal (Mestre Instalado)" className="inp" data-testid="signup-input-admin-name" />
              </Field>
              <Field label="E-mail para login *">
                <input required type="email" value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value.toLowerCase())} placeholder="veneravel@sualoja.org.br" className="inp" data-testid="signup-input-admin-email" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Senha (mín. 6) *">
                  <input required type="password" value={form.admin_password} onChange={(e) => upd("admin_password", e.target.value)} placeholder="••••••••" className="inp" data-testid="signup-input-admin-pass" />
                </Field>
                <Field label="Confirmar senha *">
                  <input required type="password" value={form.admin_password_confirm} onChange={(e) => upd("admin_password_confirm", e.target.value)} placeholder="••••••••" className="inp" data-testid="signup-input-admin-pass2" />
                </Field>
              </div>

              <div className="flex justify-between pt-4 border-t border-slate-800">
                <button
                  onClick={() => setStep(1)}
                  className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold px-4"
                >
                  ← Voltar
                </button>
                <button
                  data-testid="signup-btn-submit"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 text-white font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(16,185,129,0.25)]"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /><span>Cadastrando...</span></>
                  ) : (
                    <><Sparkles className="w-4 h-4" /><span>Cadastrar Loja</span></>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="text-center mt-5 pt-4 border-t border-slate-800/60">
            <button
              onClick={() => navigate("/")}
              className="text-[10px] uppercase text-slate-500 hover:text-[#D4AF37] font-bold tracking-widest"
            >
              Já tenho conta — Fazer login →
            </button>
          </div>
        </div>
      </div>
      <style>{`.inp{width:100%;background:#131C2E;border:1px solid #334155;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;color:#fff;outline:none}.inp:focus{border-color:#D4AF37}`}</style>
    </div>
  );
};

const StepDot = ({ n, active, done, label }) => (
  <div className="flex flex-col items-center">
    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-[11px] ${
      done ? "bg-emerald-500 border-emerald-500 text-white" :
      active ? "bg-[#D4AF37] border-[#D4AF37] text-slate-950" :
      "bg-transparent border-slate-700 text-slate-500"
    }`}>
      {done ? "✓" : n}
    </div>
    <span className={`mt-1 text-[9px] font-bold tracking-widest ${active || done ? "text-[#D4AF37]" : "text-slate-500"}`}>{label}</span>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block mb-1">{label}</label>
    {children}
  </div>
);
